import { defineComponent as _defineComponent } from 'vue'
import { resolveDirective as _resolveDirective, createVNode as _createVNode, withDirectives as _withDirectives, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-8a20dcd8"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  class: "",
  style: {"height":"100%","overflow-y":"auto","overflow-x":"hidden","box-sizing":"border-box"}
}

import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ExaminationTable from './_components/examination-table.vue'
import ExaminationDetail from '@/views/dashboard/_components/examination/examination-detail.vue'
import Card from '@/views/dashboard/_components/card/card.vue'
import StatisticsCards from '@/views/dashboard/_components/statistics-cards/statistics-cards.vue'
import type { StatisticsCardItem } from '@/views/dashboard/_components/statistics-cards/types'
import ExamScoreChart from './_components/exam-score-chart.vue'
import RankingChart, {  RankingItem } from '@/views/dashboard/_components/ranking-chart/ranking-chart.vue'
import {
  ExaminationService,
  ExaminationItem,
  ExaminationSummaryData,
  EXAM_REASON_I18N_KEY,
  FetchOptions,
} from './examination-status.service'
import { useReflectiveInjector, Type } from '@tanbo/vue-di-plugin'
import { ExaminationDetailService } from '../../_components/examination/examination-detail.service'
import { TableViewMode } from '@/views/dashboard/_enum/table-view-mode'

// 提供 DI 上下文给子组件（ExaminationTable 中通过 useReflectiveInjector() 获取）

export default /*#__PURE__*/_defineComponent({
  __name: 'examination-status',
  setup(__props) {

const injector = useReflectiveInjector([
  ExaminationService,
  {
    provide: ExaminationDetailService as Type<ExaminationDetailService>,
    useClass: ExaminationService
  }
])

const examinationService = injector.get(ExaminationService)
const { t, locale } = useI18n()

const allDrawerVisible = ref(false)
/** 抽屉内列表初始查询条件；与 drawerTableKey 联动保证图表带参打开时重新挂载 */
const drawerListFilters = ref<Partial<FetchOptions>>({})
const drawerTableKey = ref(0)
const detailDrawerVisible = ref(false)
const topTableRef = ref()
const currentDetail = ref<ExaminationItem>({} as ExaminationItem)
const currentGroupId = ref<number>(-1)
const isLoading = ref(false)
const summaryData = ref({} as ExaminationSummaryData)

function unrepairedReasonDisplay (reasonCode: string) {
  const key = EXAM_REASON_I18N_KEY[reasonCode]
  return key ? t(key) : t('examinationTable.unknownReason')
}

/** 带参打开终端列表抽屉时，标题中方括号内的说明（与 drawerListFilters 一致） */
function examDrawerFiltersBracketLabel (patch: Partial<FetchOptions>): string | null {
  const reason = patch.reason
  if (reason != null && String(reason).trim() !== '') {
    return unrepairedReasonDisplay(String(reason))
  }
  const sr = patch.score_range
  if (sr === 1) return t('examinationTable.scoreBelow60')
  if (sr === 2) return t('examinationTable.score60to99')
  if (sr === 3) return t('examinationTable.scoreFull')
  const ie = patch.is_exam
  if (ie === 1) return t('examinationTable.examined')
  if (ie === 2) return t('examinationTable.notExamined')
  return null
}

const allTerminalsDrawerTitle = computed(() => {
  void locale.value
  const base = t('examinationTable.drawerAllTerminals')
  const br = examDrawerFiltersBracketLabel(drawerListFilters.value)
  return br ? `${base} [${br}]` : base
})

/** 供 RankingChart：展示名 name + 字段 reason（与接口一致） */
const unrepairedRankingData = computed(() => {
  void locale.value
  const list = summaryData.value?.unfixed_reason_top || []
  return [...list]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map((item) => ({
      name: unrepairedReasonDisplay(item.reason),
      count: item.count,
      reason: item.reason,
    }))
})

/**
 * 统计卡片配置：顺序与 cardItems 前 3 项一致；patch 字段与 `FetchOptions` / examination-table 筛选项一致。
 */
const EXAM_CARD_DRAWER_CONFIG: Array<{
  getCount: (s: ExaminationSummaryData) => number
  patch: Partial<FetchOptions>
}> = [
  {
    getCount: (s) => s.examined_count ?? 0,
    patch: { is_exam: 1, score_range: undefined, reason: undefined },
  },
  {
    getCount: (s) => s.not_exam_count ?? 0,
    patch: { is_exam: 2, score_range: undefined, reason: undefined },
  },
  {
    getCount: (s) => s.low_score_count ?? 0,
    patch: { score_range: 1, is_exam: undefined, reason: undefined },
  },
]

const cardItems = computed<StatisticsCardItem[]>(() => {
  const s = summaryData.value
  return [
    {
      label: t('examinationTable.summaryExaminedTerminals'),
      value: s.examined_count,
      clickable: EXAM_CARD_DRAWER_CONFIG[0].getCount(s) > 0,
    },
    {
      label: t('examinationTable.summaryNotExaminedTerminals'),
      value: s.not_exam_count,
      clickable: EXAM_CARD_DRAWER_CONFIG[1].getCount(s) > 0,
    },
    {
      label: t('examinationTable.summaryHostsBelow60'),
      value: s.low_score_count,
      clickable: EXAM_CARD_DRAWER_CONFIG[2].getCount(s) > 0,
    },
    {
      label: t('examinationTable.summaryQualifiedRate'),
      value: (s.qualified_rate || 0) + '%',
      clickable: false,
    },
  ]
})

/** 指标为 0 时不打开侧栏；统一走终端列表抽屉 + initial-query-patch */
function openTerminalListDrawerIfPositive (metricCount: number, patch: Partial<FetchOptions>) {
  const n = Number(metricCount)
  if (!Number.isFinite(n) || n <= 0) return
  openDrawerWithListFilters(patch)
}

function onStatisticsCardClick (index: number) {
  const cfg = EXAM_CARD_DRAWER_CONFIG[index]
  if (!cfg) return
  openTerminalListDrawerIfPositive(cfg.getCount(summaryData.value), cfg.patch)
}

function loadExaminationSummaryData () {
  isLoading.value = true
  examinationService.getExaminationSummary({ group_id: -1, system_type: 1 }).then((res) => {
    summaryData.value = res
  }).finally(() => {
    isLoading.value = false
  })
}

onMounted(() => {
  loadExaminationSummaryData()
})

watch(allDrawerVisible, (visible) => {
  if (!visible) {
    drawerListFilters.value = {}
  }
})

function openAllExaminationDrawer() {
  drawerListFilters.value = {}
  drawerTableKey.value += 1
  allDrawerVisible.value = true
}

function openDrawerWithListFilters(patch: Partial<FetchOptions>) {
  drawerListFilters.value = patch
  drawerTableKey.value += 1
  allDrawerVisible.value = true
}

/** 分数分布饼图 slice → 与 summary.score_distribution 计数一致 */
const EXAM_SCORE_SLICE_COUNT: Record<1 | 2 | 3, keyof ExaminationSummaryData['score_distribution']> = {
  1: 'count_below_60',
  2: 'count_60_99',
  3: 'count_100',
}

function onExamScoreSliceClick (payload: { score_range: 1 | 2 | 3 }) {
  const dist = summaryData.value?.score_distribution
  if (!dist) return
  const key = EXAM_SCORE_SLICE_COUNT[payload.score_range]
  const c = dist[key] ?? 0
  openTerminalListDrawerIfPositive(c, {
    score_range: payload.score_range,
    is_exam: undefined,
    reason: undefined,
  })
}

function onExamUnrepairedRankingClick (item: RankingItem) {
  const code = item.reason as string | undefined
  if (!code) return
  openTerminalListDrawerIfPositive(Number(item.count) || 0, {
    reason: code,
    score_range: undefined,
    is_exam: undefined,
  })
}

function handleViewDetail(row: ExaminationItem) {
  currentDetail.value = row
  currentGroupId.value = row.group_id
  detailDrawerVisible.value = true
}

function handleDetailDrawerClose() {
  detailDrawerVisible.value = false
}

return (_ctx: any,_cache: any) => {
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_row = _resolveComponent("el-row")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createVNode(StatisticsCards, {
      items: cardItems.value,
      onItemClick: onStatisticsCardClick
    }, null, 8, ["items"]), [
      [_directive_loading, isLoading.value]
    ]),
    _createVNode(_component_el_row, {
      gutter: 16,
      class: "cm-ui-mt16"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(ExamScoreChart, {
              "raw-data": summaryData.value?.score_distribution,
              style: {"height":"350px"},
              onSliceClick: onExamScoreSliceClick
            }, null, 8, ["raw-data"])
          ]),
          _: 1
        }),
        _createVNode(_component_el_col, { span: 12 }, {
          default: _withCtx(() => [
            _createVNode(RankingChart, {
              title: _ctx.$t('examinationTable.chartTitleUnrepairedTop5'),
              data: unrepairedRankingData.value,
              "show-time-range": false,
              unit: _ctx.$t('portManage.unitTerminal'),
              "max-items": 5,
              "row-clickable": true,
              "name-key": "name",
              style: {"height":"350px"},
              onItemClick: onExamUnrepairedRankingClick
            }, {
              titleAfter: _withCtx(() => [
                _createVNode(_component_el_tag, {
                  size: "small",
                  type: "primary"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["title", "data", "unit"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(Card, {
      size: "second",
      class: "cm-ui-mt16",
      cardHeaderStyle: {'margin-bottom':0},
      title: _ctx.$t('examinationTable.previewListTitle')
    }, {
      titleAfter: _withCtx(() => [
        _createVNode(_component_el_tag, { size: "small" }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('common.listPreviewRecent10')), 1)
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(ExaminationTable, {
          class: "cm-ui-pt16",
          ref_key: "topTableRef",
          ref: topTableRef,
          "unfixed-reason-options": summaryData.value?.unfixed_reason_top,
          onViewAll: openAllExaminationDrawer,
          onViewDetail: handleViewDetail
        }, null, 8, ["unfixed-reason-options"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(_component_el_drawer, {
      "custom-class": "no-body-margin",
      size: "80%",
      title: allTerminalsDrawerTitle.value,
      modelValue: allDrawerVisible.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((allDrawerVisible).value = $event)),
      "destroy-on-close": "",
      "append-to-body": ""
    }, {
      default: _withCtx(() => [
        (_openBlock(), _createBlock(ExaminationTable, {
          key: drawerTableKey.value,
          "view-mode": _unref(TableViewMode).full,
          "initial-query-patch": drawerListFilters.value,
          "unfixed-reason-options": summaryData.value?.unfixed_reason_top,
          onViewDetail: handleViewDetail,
          onUpdatePreview: topTableRef.value.getList
        }, null, 8, ["view-mode", "initial-query-patch", "unfixed-reason-options", "onUpdatePreview"]))
      ]),
      _: 1
    }, 8, ["title", "modelValue"]),
    _createVNode(_component_el_drawer, {
      size: "40%",
      modelValue: detailDrawerVisible.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((detailDrawerVisible).value = $event)),
      "before-close": handleDetailDrawerClose,
      title: _ctx.$t('examinationTable.examDetailTitle'),
      "destroy-on-close": "",
      "append-to-body": true
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createVNode(ExaminationDetail, {
            "base-info": currentDetail.value,
            "group-id": currentGroupId.value,
            onCloseDrawer: handleDetailDrawerClose
          }, null, 8, ["base-info", "group-id"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})